import { UploadOutlined } from '@ant-design/icons';
import type { ProFormInstance } from '@ant-design/pro-components';
import {
  ModalForm,
  ProFormCheckbox,
  ProFormDatePicker,
  ProFormDateRangePicker,
  ProFormDateTimePicker,
  ProFormDateTimeRangePicker,
  ProFormDigit,
  ProFormItem,
  ProFormRadio,
  ProFormSelect,
  ProFormSwitch,
  ProFormText,
  ProFormTextArea,
  ProFormUploadButton,
} from '@ant-design/pro-components';
import { request } from '@umijs/max';
import { Button, message, Progress, Space, Upload } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import type { CommonModalFormProps, ModalFormFieldConfig } from './types';

// 切片大小：5MB
const CHUNK_SIZE = 5 * 1024 * 1024;

// 生成唯一ID
const generateUploadId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// 大文件切片上传
async function uploadChunk(params: {
  file: Blob;
  uploadId: string;
  fileName: string;
  chunkIndex: number;
  chunkTotal: number;
  chunkSize: number;
  totalSize: number;
  fileMd5?: string;
  action?: string;
}) {
  const formData = new FormData();
  // 第三参数必须带文件名，否则 Tomcat 可能把分片当非文件参数计入 maxPostSize(默认2MB)
  formData.append('file', params.file, params.fileName || 'chunk.bin');
  formData.append('uploadId', params.uploadId);
  formData.append('fileName', params.fileName);
  formData.append('chunkIndex', String(params.chunkIndex));
  formData.append('chunkTotal', String(params.chunkTotal));
  formData.append('chunkSize', String(params.chunkSize));
  formData.append('totalSize', String(params.totalSize));
  if (params.fileMd5) {
    formData.append('fileMd5', params.fileMd5);
  }

  const res = await request(params.action || '/admin/Main/uploadLarge', {
    method: 'POST',
    data: formData,
  });
  return res;
}

// 计算文件MD5
async function getFileMd5(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const arrayBuffer = reader.result as ArrayBuffer;
      // 简单的 hash 实现
      const hashBuffer = arrayBuffer.slice(
        0,
        Math.min(1024 * 1024, arrayBuffer.byteLength),
      );
      const hashValue = Array.from(new Uint8Array(hashBuffer as ArrayBuffer))
        .map((b) => b.toString(16).padStart(2, '0'))
        .join('');
      resolve(hashValue);
    };
    reader.onerror = reject;
    reader.readAsArrayBuffer(file.slice(0, Math.min(1024 * 1024, file.size)));
  });
}

// 大文件上传组件 Props
interface LargeFileUploaderProps {
  value?: string;
  onChange?: (value: string) => void;
  accept?: string;
  maxSize?: number; // 最大文件大小，默认 500MB
  action?: string; // 上传接口地址
  disabled?: boolean;
  label?: string;
}

// 大文件上传组件
const LargeFileUploader: React.FC<LargeFileUploaderProps> = ({
  value,
  onChange,
  accept = '.zip,.7z,.rar,.onnx,.pt,.pth',
  maxSize = 500 * 1024 * 1024,
  action = '/admin/Main/uploadLarge',
  disabled,
  label = '选择文件',
}) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fileName, setFileName] = useState<string>('');

  // 初始化文件信息
  useEffect(() => {
    if (value) {
      setFileName(value.split('/').pop() || '');
    } else {
      setFileName('');
    }
  }, [value]);

  const handleUpload = async (file: File) => {
    if (file.size > maxSize) {
      message.error(`文件大小不能超过 ${Math.round(maxSize / 1024 / 1024)}MB`);
      return false;
    }

    setUploading(true);
    setProgress(0);
    setFileName(file.name);

    try {
      const totalSize = file.size;
      const chunkTotal = Math.ceil(totalSize / CHUNK_SIZE);
      const uploadId = generateUploadId();

      // 计算 MD5（简化版）
      let fileMd5 = '';
      try {
        fileMd5 = await getFileMd5(file);
      } catch {
        console.log('MD5 计算失败，跳过');
      }

      let finalUrl = '';

      // 逐片上传
      for (let i = 0; i < chunkTotal; i++) {
        const start = i * CHUNK_SIZE;
        const end = Math.min(start + CHUNK_SIZE, totalSize);
        const chunk = file.slice(start, end);
        const chunkIndex = i + 1;

        const res = await uploadChunk({
          file: chunk,
          uploadId,
          fileName: file.name,
          chunkIndex,
          chunkTotal,
          chunkSize: end - start,
          totalSize,
          fileMd5,
          action,
        });

        if (res.code !== 0 && res.code !== 200) {
          throw new Error(res.msg || res.message || '上传失败');
        }

        // 更新进度
        setProgress(Math.round(((i + 1) / chunkTotal) * 100));

        // 检查是否返回了文件URL（最后一个分片）
        if (res.data?.fileUrl) {
          finalUrl = res.data.fileUrl;
        }
      }

      if (finalUrl) {
        onChange?.(finalUrl);
        message.success('上传成功');
      } else {
        throw new Error('上传完成但未返回文件URL');
      }
    } catch (error: any) {
      message.error(error.message || '上传失败');
      setFileName('');
      onChange?.('');
    } finally {
      setUploading(false);
    }

    return false; // 阻止默认上传行为
  };

  const handleRemove = () => {
    setFileName('');
    onChange?.('');
  };

  return (
    <div>
      <Upload
        accept={accept}
        showUploadList={false}
        beforeUpload={handleUpload}
        disabled={disabled || uploading}
      >
        <Button
          icon={<UploadOutlined />}
          loading={uploading}
          disabled={disabled || uploading || !!fileName}
        >
          {uploading ? '上传中...' : label}
        </Button>
      </Upload>

      {fileName && (
        <div style={{ marginTop: 8 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '8px 12px',
              background: '#f5f5f5',
              borderRadius: 4,
              maxWidth: 400,
            }}
          >
            <span
              style={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {fileName}
            </span>
            <Button
              type="link"
              danger
              size="small"
              style={{ marginLeft: 8 }}
              onClick={handleRemove}
              disabled={uploading}
            >
              删除
            </Button>
          </div>
        </div>
      )}

      {uploading && (
        <Progress
          percent={progress}
          style={{ marginTop: 8, maxWidth: 400 }}
          status={progress === 100 ? 'success' : 'active'}
        />
      )}
    </div>
  );
};

const CommonModalForm: React.FC<CommonModalFormProps> = (props) => {
  const {
    title = '新建',
    width = '600px',
    open = false,
    onOpenChange,
    fields,
    initialValues,
    onFinish,
    onCancel,
    submitText = '确定',
    cancelText = '取消',
    showCancel = true,
    showSubmit = true,
    customActions = [],
    onBeforeSubmit,
    onAfterSubmit,
    closeOnSuccess = true,
    resetOnClose = true,
    layout = 'horizontal',
    labelCol = { span: 6 },
    wrapperCol = { span: 18 },
    ...restProps
  } = props;

  const formRef = useRef<ProFormInstance | null>(null);

  // 重置表单
  useEffect(() => {
    if (!open && resetOnClose && formRef.current) {
      formRef.current.resetFields();
    }
  }, [open, resetOnClose]);

  // 渲染表单字段
  const renderFormField = (field: ModalFormFieldConfig) => {
    const {
      name,
      label,
      type = 'text',
      required = false,
      placeholder,
      formItemProps,
      render,
      options = [],
      initialValue,
      width: fieldWidth,
      rules = [],
      disabled = false,
      readonly = false,
      tooltip,
      extraProps = {},
    } = field;

    // 构建基础属性
    // 合并 rules：优先使用 formItemProps 中的 rules，如果没有则使用字段配置的 rules
    const finalRules =
      formItemProps?.rules ||
      (required
        ? [{ required: true, message: `请输入${label}` }, ...rules]
        : rules);

    const baseProps: Record<string, any> = {
      name,
      label,
      placeholder: placeholder || `请输入${label}`,
      disabled: disabled || readonly,
      tooltip,
      initialValue,
      rules: finalRules,
      ...formItemProps,
      ...extraProps,
    };

    /**
     * 自适应弹窗宽度：
     * - ModalForm 本身会根据 width 控制弹窗宽度
     * - ProForm 的 width（xs/sm/md/lg/xl）更偏向栅格/工具栏场景（比如 inline）
     * - 因此默认不传 width，让表单项占满弹窗内容区；如果业务方显式传 field.width 则尊重
     */
    if (fieldWidth) {
      baseProps.width = fieldWidth;
    }

    // 如果有自定义渲染，使用自定义渲染
    if (render) {
      return render();
    }

    // 根据类型渲染不同的表单项
    switch (type) {
      case 'textarea':
        return <ProFormTextArea {...baseProps} />;
      case 'number':
        return <ProFormDigit {...baseProps} />;
      case 'select':
        return (
          <ProFormSelect
            {...baseProps}
            options={options}
            placeholder={placeholder || `请选择${label}`}
            mode={extraProps?.mode}
          />
        );
      case 'radio':
        return (
          <ProFormRadio.Group
            {...baseProps}
            options={options}
            placeholder={placeholder || `请选择${label}`}
          />
        );
      case 'checkbox':
        return (
          <ProFormCheckbox.Group
            {...baseProps}
            options={options}
            placeholder={placeholder || `请选择${label}`}
          />
        );
      case 'date':
        return <ProFormDatePicker {...baseProps} />;
      case 'dateTime':
        return <ProFormDateTimePicker {...baseProps} />;
      case 'dateRange':
        return <ProFormDateRangePicker {...baseProps} />;
      case 'dateTimeRange':
        return <ProFormDateTimeRangePicker {...baseProps} />;
      case 'switch':
        return <ProFormSwitch {...baseProps} />;
      case 'upload':
        // React 19 + TS 下，ProFormUploadButton 类型可能被推断为 ForwardRefRenderFunction，导致 JSX 报错
        return React.createElement(ProFormUploadButton as any, baseProps);
      case 'uploadLarge':
        return (
          <ProFormItem key={name} name={name} label={label} rules={finalRules}>
            <LargeFileUploader
              accept={extraProps?.accept}
              maxSize={extraProps?.maxSize}
              action={extraProps?.action}
              disabled={disabled || readonly}
              label={extraProps?.buttonText || '选择文件'}
            />
          </ProFormItem>
        );
      case 'password':
        return <ProFormText.Password {...baseProps} />;
      case 'text':
      default:
        return <ProFormText {...baseProps} />;
    }
  };

  // 处理提交
  const handleFinish = async (values: Record<string, any>) => {
    try {
      // 提交前验证
      if (onBeforeSubmit) {
        const canSubmit = await Promise.resolve(onBeforeSubmit(values));
        if (!canSubmit) {
          return false;
        }
      }

      // 执行提交
      let success = true;
      if (onFinish) {
        success = await Promise.resolve(onFinish(values));
      }

      // 提交后回调
      if (onAfterSubmit) {
        onAfterSubmit(success, values);
      }

      // 成功后关闭弹窗
      if (success && closeOnSuccess) {
        onOpenChange?.(false);
      }

      return success;
    } catch (error) {
      console.error('Form submit error:', error);
      return false;
    }
  };

  // 处理取消
  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      onOpenChange?.(false);
    }
  };

  // 构建操作按钮
  const buildActions = (submit: () => void) => {
    const actions: React.ReactNode[] = [];

    // 取消按钮
    if (showCancel) {
      actions.push(
        <Button key="cancel" onClick={handleCancel}>
          {cancelText}
        </Button>,
      );
    }

    // 提交按钮
    if (showSubmit) {
      actions.push(
        <Button key="submit" type="primary" onClick={submit}>
          {submitText}
        </Button>,
      );
    }

    // 自定义操作按钮
    customActions.forEach((action, index) => {
      if (action.visible !== false) {
        actions.push(
          <Button
            key={action.label || index}
            type={action.type}
            danger={action.danger}
            disabled={action.disabled}
            icon={action.icon}
            onClick={async () => {
              if (action.onClick && formRef.current) {
                const values = await formRef.current.validateFields();
                await action.onClick(values);
              }
            }}
          >
            {action.label}
          </Button>,
        );
      }
    });

    return actions.length > 0 ? <Space>{actions}</Space> : null;
  };

  return (
    <ModalForm
      title={title}
      width={width}
      open={open}
      onOpenChange={onOpenChange}
      onFinish={handleFinish}
      initialValues={initialValues}
      formRef={formRef}
      layout={layout}
      labelCol={labelCol}
      wrapperCol={wrapperCol}
      modalProps={{
        destroyOnClose: true,
        ...restProps.modalProps,
      }}
      submitter={{
        render: (props) => buildActions(props.submit),
      }}
      {...restProps}
    >
      {fields.map((field, index) => (
        <React.Fragment key={field.name || index}>
          {renderFormField(field)}
        </React.Fragment>
      ))}
    </ModalForm>
  );
};

export default CommonModalForm;
export type {
  CommonModalFormProps,
  ModalFormActionConfig,
  ModalFormFieldConfig,
} from './types';
